
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Download, Link } from 'lucide-react';
import { toast } from 'sonner';
import { validateAndFetchArxivMetadata } from '@/lib/api/arxiv-metadata';
import { useArxivImport } from './hooks/useArxivImport';
import { ArxivRecord } from './types';

const ArxivDirectImport = () => {
  const [arxivUrl, setArxivUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [paperData, setPaperData] = useState<ArxivRecord | null>(null);
  const { importPaper, isImporting } = useArxivImport();

  const handleFetchPaper = async () => {
    if (!arxivUrl.trim()) {
      toast.error('Please enter an arXiv URL');
      return;
    }

    setLoading(true);
    try {
      const result = await validateAndFetchArxivMetadata(arxivUrl);
      
      if (!result.isValid || !result.metadata) {
        toast.error('Invalid arXiv URL or paper not found', {
          description: result.errors?.join(', ') || 'Could not fetch paper metadata'
        });
        return;
      }

      // Extract arXiv ID from URL
      const arxivIdMatch = arxivUrl.match(/arxiv\.org\/(?:abs|pdf)\/(\d+\.\d+)/);
      const arxivId = arxivIdMatch?.[1] || '';

      // Convert metadata to ArxivRecord format
      const record: ArxivRecord = {
        id: arxivId,
        title: result.metadata.title,
        abstract: result.metadata.abstract,
        authors: result.metadata.authors,
        categories: result.metadata.categories,
        publishedDate: result.metadata.published,
        updatedDate: result.metadata.published, // Use published date as updated date for direct imports
        versions: 1,
        pdfUrl: `https://arxiv.org/pdf/${arxivId}.pdf`,
        doi: result.metadata.doi,
        license: result.metadata.license?.name || 'Unknown'
      };

      setPaperData(record);
      toast.success('Paper metadata fetched successfully!');
    } catch (error) {
      console.error('Error fetching arXiv paper:', error);
      toast.error('Failed to fetch paper metadata');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!paperData) return;
    await importPaper(paperData);
    // Clear the form after successful import
    setArxivUrl('');
    setPaperData(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Link className="h-5 w-5" />
          Direct arXiv Paper Import
        </CardTitle>
        <CardDescription>
          Import a specific paper by providing its arXiv URL (e.g., https://arxiv.org/abs/2301.00001)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="arxiv-url">arXiv URL</Label>
          <div className="flex gap-2">
            <Input
              id="arxiv-url"
              type="url"
              placeholder="https://arxiv.org/abs/2301.00001"
              value={arxivUrl}
              onChange={(e) => setArxivUrl(e.target.value)}
              className="flex-1"
            />
            <Button 
              onClick={handleFetchPaper} 
              disabled={loading}
              className="flex items-center gap-2"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Fetch
            </Button>
          </div>
        </div>

        {paperData && (
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-4">
              <div className="space-y-3">
                <div>
                  <h3 className="font-semibold text-lg leading-tight">{paperData.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    arXiv:{paperData.id} • Published: {formatDate(paperData.publishedDate)}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Authors:</p>
                  <p className="text-sm">{paperData.authors.join(', ')}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Categories:</p>
                  <p className="text-sm">{paperData.categories.join(', ')}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Abstract:</p>
                  <p className="text-sm text-muted-foreground line-clamp-3">
                    {paperData.abstract}
                  </p>
                </div>
                
                {paperData.license && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">License:</p>
                    <Badge variant="outline" className="text-xs">
                      {paperData.license}
                    </Badge>
                  </div>
                )}
                
                <div className="flex gap-2 pt-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => window.open(paperData.pdfUrl, '_blank')}
                    className="flex items-center gap-1"
                  >
                    <Download className="h-3 w-3" />
                    PDF
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => window.open(`https://arxiv.org/abs/${paperData.id}`, '_blank')}
                    className="flex items-center gap-1"
                  >
                    <Link className="h-3 w-3" />
                    arXiv Page
                  </Button>
                  <Button 
                    size="sm" 
                    variant="default"
                    onClick={handleImport}
                    disabled={isImporting}
                    className="flex items-center gap-1 ml-auto"
                  >
                    {isImporting ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Download className="h-3 w-3" />
                    )}
                    {isImporting ? 'Importing...' : 'Import to Database'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
};

export default ArxivDirectImport;
